import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { LogIn, Eye, EyeOff } from "lucide-react";

const LOGO_URL = process.env.REACT_APP_LOGO_URL;

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(user.role === "super_admin" ? "/admin" : "/portal", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please enter email and password"); return; }
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success(`Welcome back, ${data.name || "User"}!`);
      navigate(data.role === "super_admin" ? "/admin" : "/portal", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {LOGO_URL && <img src={LOGO_URL} alt="OSAA" className="h-16 mx-auto mb-4" />}
          <h1 className="font-heading text-3xl font-medium text-foreground tracking-tight">Welcome to OSAA Portal</h1>
          <p className="text-sm text-muted-foreground mt-2">Open Sky Adventure Academy-School Management Portal</p>
        </div>

        <div className="bg-card border border-border/60 p-8 rounded-sm shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Email Address</label>
              <Input data-testid="login-email" type="email" placeholder="you@school.com" value={email}
                onChange={(e) => setEmail(e.target.value)} className="border-border/60" />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Password</label>
              <div className="relative">
                <Input data-testid="login-password" type={showPwd ? "text" : "password"} placeholder="Enter password"
                  value={password} onChange={(e) => setPassword(e.target.value)} className="border-border/60 pr-10" />
                <button type="button" onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-medium text-sm rounded-sm hover:bg-primary/90 transition-all disabled:opacity-50">
              <LogIn className="w-4 h-4" />
              {loading ? "Signing in..." : "Sign In"}
            </button>
            <div className="text-center">
              <Link to="/reset-password" className="text-xs text-muted-foreground hover:text-primary transition-colors">Forgot password?</Link>
            </div>
          </form>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-6">Secure portal for authorized schools only</p>
      </div>
    </div>
  );
}
